import { Component, OnDestroy, OnInit, Inject } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { Subscription } from "rxjs";

import { DirectionService } from "../../direction.service";
import { getPhSystemIconSrcFromProduct } from "../../main-section/ph-printing-table/ph-service-data";

export interface BillingTransactionRow {
  _id?: string;
  responseText?: string;
  responseCode?: string;
  sum?: number;
  user?: string;
  orderID?: string | { _id?: string };
  created?: string | Date;
}

export interface BillingErrorsDialogData {
  mode?: 'order' | 'user';
  order?: any;
  user?: any;
  transactions: BillingTransactionRow[];
}

@Component({
  selector: "app-billing-errors-dialog",
  templateUrl: "./billing-errors-dialog.component.html",
  styleUrls: ["./billing-errors-dialog.component.css"],
  host: {
    class: "fill-screen-modal-phone",
  },
})
export class BillingErrorsDialogComponent implements OnInit, OnDestroy {
  isRTL = true;
  isDarkMode = false;
  private directionSub?: Subscription;
  private darkSub?: Subscription;

  mode: 'order' | 'user' = 'order';
  order: any;
  user: any;
  transactions: BillingTransactionRow[];

  constructor(
    private directionService: DirectionService,
    private dialogRef: MatDialogRef<BillingErrorsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: BillingErrorsDialogData
  ) {
    this.mode = data?.mode === 'user' ? 'user' : 'order';
    this.order = data?.order ?? null;
    this.user = data?.user ?? null;
    this.transactions = Array.isArray(data?.transactions) ? data.transactions : [];
  }

  get showOrderHeader(): boolean {
    return this.mode === 'order';
  }

  get showUserContactHeader(): boolean {
    return this.mode === 'user' || this.mode === 'order';
  }

  get isUserMode(): boolean {
    return this.mode === 'user';
  }

  ngOnInit(): void {
    this.directionSub = this.directionService.direction$.subscribe((dir) => {
      this.isRTL = dir === "rtl";
    });
    this.darkSub = this.directionService.isDarkMode$.subscribe((d) => {
      this.isDarkMode = d;
    });
  }

  ngOnDestroy(): void {
    this.directionSub?.unsubscribe();
    this.darkSub?.unsubscribe();
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  /** userPhone על ההזמנה לעיתים מספר — לא מחרוזת */
  private formatDisplayValue(value: unknown): string {
    if (value == null || value === "") {
      return "—";
    }
    const s = String(value).trim();
    return s.length ? s : "—";
  }

  get userEmail(): string {
    if (this.mode === 'user') {
      return this.formatDisplayValue(this.user?.email);
    }
    return this.formatDisplayValue(this.order?.user ?? this.order?.userEmail);
  }

  get userPhone(): string {
    const raw =
      this.mode === 'user'
        ? this.user?.phone
        : (this.order?.userID?.phone ?? this.order?.user?.phone);
    return this.formatPhoneDisplay(raw);
  }

  /** תצוגת טלפון — תמיד עם 0 בהתחלה (כמו ניהול הזמנות) */
  private formatPhoneDisplay(value: unknown): string {
    const base = this.formatDisplayValue(value);
    if (base === "—") {
      return base;
    }
    const digits = base.replace(/\s/g, "");
    return digits.startsWith("0") ? digits : `0${digits}`;
  }

  get orderIdShort(): string {
    const id = this.order?._id;
    return id ? String(id).slice(0, 8) : "—";
  }

  isPHOrder(order: any): boolean {
    return !!order?.collectionData;
  }

  getIsPics(printer: any): string {
    return printer?.isPicsPrinter ? "pics" : "express";
  }

  getPhSystemIconSrcForOrder(order: any): string {
    if (!this.isPHOrder(order)) {
      return "assets/images/phprint-icon.svg";
    }
    return getPhSystemIconSrcFromProduct(order?.productID);
  }

  getBranchNamingKey(order: any): string | null {
    if (!order || this.isPHOrder(order)) {
      return null;
    }
    if (!order.branchID?.is_express) {
      const sn = order.branchID?.serial_name;
      return sn ? `choose-branch.naming.plotter.${sn}` : null;
    }
    const sn = order.printerID?.serial_name;
    return sn ? `choose-branch.naming.express.${sn}` : null;
  }

  formatCreated(created: string | Date | undefined): string {
    if (!created) {
      return "—";
    }
    try {
      const d = created instanceof Date ? created : new Date(created);
      if (Number.isNaN(d.getTime())) {
        return "—";
      }
      return d.toLocaleString();
    } catch {
      return "—";
    }
  }

  formatSum(sum: number | undefined): string {
    if (sum == null || Number.isNaN(Number(sum))) {
      return "—";
    }
    return Number(sum).toFixed(2);
  }

  formatOrderIdShort(orderId: string | { _id?: string } | null | undefined): string {
    if (!orderId) {
      return "—";
    }
    const id = typeof orderId === "object" ? orderId._id : orderId;
    return id ? String(id).slice(0, 8) : "—";
  }
}
